import {
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";

export default function useFaceCamera(
  modelsLoaded
) {
  const videoRef = useRef(null);

  const streamRef = useRef(null);

  /* ---------------------------------- */
  /* LOCKS */
  /* ---------------------------------- */

  const startingRef = useRef(false);

  const loadingRef = useRef(false);

  /* ---------------------------------- */
  /* STATE */
  /* ---------------------------------- */

  const [cameraOn, setCameraOn] =
    useState(false);

  const [status, setStatus] =
    useState("");

  const [devices, setDevices] =
    useState([]);

  const [
    selectedDeviceId,
    setSelectedDeviceId,
  ] = useState("");

  /* ---------------------------------- */
  /* HARD RESET CAMERA */
  /* ---------------------------------- */

  const hardResetCamera =
    useCallback(async () => {
      try {
        if (streamRef.current) {
          streamRef.current
            .getTracks()
            .forEach((track) => {
              try {
                track.stop();
              } catch {}
            });

          streamRef.current = null;
        }

        const video =
          videoRef.current;

        if (video) {
          try {
            video.pause();
          } catch {}

          try {
            video.srcObject =
              null;
          } catch {}
        }

        // ⚡ smaller delay
        await new Promise(
          (resolve) =>
            setTimeout(
              resolve,
              300
            )
        );
      } catch (err) {
        console.error(
          "Hard reset error:",
          err
        );
      }
    }, []);

  /* ---------------------------------- */
  /* LOAD DEVICES */
  /* ---------------------------------- */

  const loadDevices =
    useCallback(async () => {
      if (loadingRef.current)
        return;

      loadingRef.current = true;

      try {
        /* ------------------------------ */
        /* CAMERA WARMUP */
        /* ------------------------------ */

        try {
          const tempStream =
            await navigator.mediaDevices.getUserMedia(
              {
               video: {
  facingMode: "user",
  width: { ideal: 1280 },
  height: { ideal: 720 },
},
                audio: false,
              }
            );

          tempStream
            .getTracks()
            .forEach((t) =>
              t.stop()
            );

          await new Promise(
            (resolve) =>
              setTimeout(
                resolve,
                500
              )
          );
        } catch (err) {
          console.warn(
            "Warmup skipped:",
            err?.name
          );
        }

        /* ------------------------------ */
        /* ENUMERATE */
        /* ------------------------------ */

        const allDevices =
          await navigator.mediaDevices.enumerateDevices();

        const cams =
          allDevices.filter(
            (d) =>
              d.kind ===
              "videoinput"
          );

        setDevices(cams);

        /* ------------------------------ */
        /* AUTO SELECT BEST CAMERA */
        /* ------------------------------ */

        if (cams.length > 0) {
          const stillValid =
            cams.find(
              (d) =>
                d.deviceId ===
                selectedDeviceId
            );

          if (!stillValid) {
            const preferred =
              cams.find((d) =>
                d.label
                  ?.toLowerCase()
                  .includes(
                    "front"
                  )
              ) ||
              cams.find((d) =>
                d.label
                  ?.toLowerCase()
                  .includes(
                    "user"
                  )
              ) ||
              cams[0];

            setSelectedDeviceId(
              preferred.deviceId
            );
          }
        }
      } catch (err) {
        console.error(
          "Device load error:",
          err
        );

        setStatus(
          "Camera devices error ❌"
        );
      } finally {
        loadingRef.current = false;
      }
    }, [selectedDeviceId]);

  /* ---------------------------------- */
  /* INIT CAMERA */
  /* ---------------------------------- */
const initCamera =
    useCallback(async () => {
      if (
        startingRef.current ||
        streamRef.current ||
        !modelsLoaded
      ) {
        return;
      }

      // 1. Safely grab the video element at the very top of the scope
      const currentVideoElement = videoRef.current;
      if (!currentVideoElement) {
        console.error("Video element missing during initialization");
        return;
      }

      startingRef.current = true;

      try {
        setStatus(
          "Starting camera..."
        );

        await hardResetCamera();

        /* ------------------------------ */
        /* REFRESH DEVICES */
        /* ------------------------------ */

        const allDevices =
          await navigator.mediaDevices.enumerateDevices();

        const cams =
          allDevices.filter(
            (d) =>
              d.kind ===
              "videoinput"
          );

        if (!cams.length) {
          throw new Error(
            "No camera device found"
          );
        }

        /* ------------------------------ */
        /* VALIDATE CAMERA */
        /* ------------------------------ */

        const validDevice =
          cams.find(
            (d) =>
              d.deviceId ===
              selectedDeviceId
          );

        /* ------------------------------ */
        /* CONSTRAINTS */
        /* ------------------------------ */

        const constraints = {
          video: {
            deviceId: validDevice ? { ideal: validDevice.deviceId } : undefined,
            facingMode: { ideal: "user" },
            width: 1280,
            height: 720,
            frameRate: { ideal: 24, max: 30 },
          },
          audio: false,
        };

        /* ------------------------------ */
        /* FALLBACK CAMERA */
        /* ------------------------------ */

        if (
          !validDevice &&
          cams[0]
        ) {
          setSelectedDeviceId(
            cams[0].deviceId
          );
        }

        /* ------------------------------ */
        /* GET STREAM WITH SAFARI CATCH */
        /* ------------------------------ */

        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
          if (err.name === "OverconstrainedError") {
            console.warn("Safari resolution rejection. Falling back to default configuration.");
            
            // Using absolute basic native keys to bypass any legacy compiler snags
            const fallbackOptions = { audio: false };
            fallbackOptions["video"] = validDevice ? { deviceId: validDevice.deviceId } : true;
            
            stream = await navigator.mediaDevices.getUserMedia(fallbackOptions);
          } else {
            throw err;
          }
        }

        streamRef.current = stream;
        currentVideoElement.srcObject = stream;

        /* ------------------------------ */
        /* SAFARI + MOBILE FIX */
        /* ------------------------------ */

        await new Promise(
          (resolve) => {
            let done = false;

            const complete =
              async () => {
                if (done) return;

                done = true;

                currentVideoElement.removeEventListener(
                  "loadedmetadata",
                  complete
                );

                currentVideoElement.removeEventListener(
                  "canplay",
                  complete
                );

                try {
                  await currentVideoElement.play();
                } catch (err) {
                  console.warn(
                    "Video play warning:",
                    err
                  );
                }

                // ⚡ autofocus settle
                await new Promise(
                  (r) =>
                    setTimeout(
                      r,
                      1200
                    )
                );

                resolve();
              };

            currentVideoElement.addEventListener(
              "loadedmetadata",
              complete
            );

            currentVideoElement.addEventListener(
              "canplay",
              complete
            );
          }
        );

        setStatus(
          "Camera started ✅"
        );
      } catch (err) {
        console.error(
          "Camera init error:",
          err
        );

        console.log(
          "Selected Device:",
          selectedDeviceId
        );

        try {
          const devices =
            await navigator.mediaDevices.enumerateDevices();

          console.log(
            "Available Devices:",
            devices
          );
        } catch {}

        setStatus(
          `Camera error ❌ ${
            err?.name || ""
          }`
        );
      } finally {
        startingRef.current = false;
      }
    }, [
      modelsLoaded,
      selectedDeviceId,
      hardResetCamera,
    ]);

  /* ---------------------------------- */
  /* START */
  /* ---------------------------------- */

  const startCamera =
    useCallback(() => {
      setCameraOn(true);
    }, []);

  /* ---------------------------------- */
  /* STOP */
  /* ---------------------------------- */

  const stopCamera =
    useCallback(async () => {
      await hardResetCamera();

      setCameraOn(false);

      setStatus("Stopped");
    }, [hardResetCamera]);

  /* ---------------------------------- */
  /* LOAD DEVICES */
  /* ---------------------------------- */

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  /* ---------------------------------- */
  /* START CAMERA */
  /* ---------------------------------- */

  useEffect(() => {
    if (
      cameraOn &&
      modelsLoaded
    ) {
      initCamera();
    }
  }, [
    cameraOn,
    modelsLoaded,
    initCamera,
  ]);

  /* ---------------------------------- */
  /* CLEANUP */
  /* ---------------------------------- */

  useEffect(() => {
    return () => {
      hardResetCamera();
    };
  }, [hardResetCamera]);

  /* ---------------------------------- */
  /* EXPORTS */
  /* ---------------------------------- */

  return {
    videoRef,

    cameraOn,

    startCamera,

    stopCamera,

    status,

    devices,

    selectedDeviceId,

    setSelectedDeviceId,

    getVideoElement: () =>
      videoRef.current,
  };
}